import { useState, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { usePersonalBudgets } from '@/hooks';
import { usePersonalTransactions } from '@/hooks';
import { usePersonalLoans } from '@/hooks';
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Loader2,
  FileBarChart,
  Banknote,
} from 'lucide-react';

function formatCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function getMonthRange(year: number, month: number): { dateFrom: string; dateTo: string } {
  const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { dateFrom, dateTo };
}

function getMonthLabel(year: number, month: number): string {
  return new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

type BudgetStatus = 'on-track' | 'warning' | 'over';

function getStatus(percentage: number, warningThreshold: number, criticalThreshold: number): BudgetStatus {
  if (percentage >= criticalThreshold) return 'over';
  if (percentage >= warningThreshold) return 'warning';
  return 'on-track';
}

function getStatusColor(status: BudgetStatus): string {
  switch (status) {
    case 'over': return 'bg-sapling-error-100';
    case 'warning': return 'bg-sapling-warning-100';
    case 'on-track': return 'bg-sapling-primary-100';
  }
}

function getStatusTextColor(status: BudgetStatus): string {
  switch (status) {
    case 'over': return 'text-sapling-error-100';
    case 'warning': return 'text-sapling-warning-100';
    case 'on-track': return 'text-sapling-primary-100';
  }
}

interface CategorySpending {
  categoryId: string | null;
  categoryName: string;
  categoryColor: string | null;
  income: number;
  expense: number;
}

export function BudgetReportsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const { dateFrom, dateTo } = getMonthRange(year, month);
  const monthLabel = getMonthLabel(year, month);

  const { budgets, isLoading: budgetsLoading } = usePersonalBudgets();
  const { transactions, isLoading: txLoading } = usePersonalTransactions({
    dateFrom,
    dateTo,
    limit: 1000,
  });
  const { loans, isLoading: loansLoading } = usePersonalLoans();

  const isLoading = budgetsLoading || txLoading || loansLoading;

  // Debt summary
  const activeLoans = loans.filter(l => l.status === 'active');
  const totalDebt = activeLoans.reduce((sum, l) => sum + parseFloat(l.current_balance), 0);
  const totalMonthlyPayments = activeLoans.reduce(
    (sum, l) => sum + (l.minimum_payment ? parseFloat(l.minimum_payment) : 0),
    0
  );

  // Navigate months
  const goToPrevMonth = () => {
    if (month === 0) {
      setYear(y => y - 1);
      setMonth(11);
    } else {
      setMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 11) {
      setYear(y => y + 1);
      setMonth(0);
    } else {
      setMonth(m => m + 1);
    }
  };

  // Aggregate transactions by category
  const categorySpending = useMemo(() => {
    const map = new Map<string | null, CategorySpending>();

    for (const tx of transactions) {
      const key = tx.personal_category_id;
      const existing = map.get(key);

      if (existing) {
        if (tx.transaction_type === 'income') {
          existing.income += parseFloat(tx.amount);
        } else if (tx.transaction_type === 'expense') {
          existing.expense += parseFloat(tx.amount);
        }
      } else {
        map.set(key, {
          categoryId: key,
          categoryName: tx.category_name || 'Uncategorized',
          categoryColor: tx.category_color,
          income: tx.transaction_type === 'income' ? parseFloat(tx.amount) : 0,
          expense: tx.transaction_type === 'expense' ? parseFloat(tx.amount) : 0,
        });
      }
    }

    return map;
  }, [transactions]);

  // Income rows: categories with income transactions
  const incomeRows = useMemo(() => {
    const rows: { categoryName: string; categoryColor: string | null; actual: number }[] = [];

    categorySpending.forEach(cs => {
      if (cs.income > 0) {
        rows.push({
          categoryName: cs.categoryName,
          categoryColor: cs.categoryColor,
          actual: cs.income,
        });
      }
    });

    return rows.sort((a, b) => b.actual - a.actual);
  }, [categorySpending]);

  // Expense rows: merge budgets with spending
  const expenseRows = useMemo(() => {
    const rows: {
      categoryName: string;
      categoryColor: string | null;
      budget: number | null;
      spent: number;
      percentage: number;
      status: BudgetStatus;
      warningThreshold: number;
      criticalThreshold: number;
    }[] = [];

    // Track which categories we've covered via budgets
    const coveredCategoryIds = new Set<string>();

    for (const budget of budgets) {
      coveredCategoryIds.add(budget.category_id);
      const spending = categorySpending.get(budget.category_id);
      const spent = spending?.expense ?? 0;
      const limit = parseFloat(budget.limit_amount);
      const percentage = limit > 0 ? (spent / limit) * 100 : 0;

      rows.push({
        categoryName: budget.category_name,
        categoryColor: budget.category_color,
        budget: limit,
        spent,
        percentage,
        status: getStatus(percentage, budget.alert_threshold_warning, budget.alert_threshold_critical),
        warningThreshold: budget.alert_threshold_warning,
        criticalThreshold: budget.alert_threshold_critical,
      });
    }

    // Add categories with spending but no budget
    categorySpending.forEach(cs => {
      if (cs.expense > 0 && cs.categoryId && !coveredCategoryIds.has(cs.categoryId)) {
        rows.push({
          categoryName: cs.categoryName,
          categoryColor: cs.categoryColor,
          budget: null,
          spent: cs.expense,
          percentage: 0,
          status: 'on-track',
          warningThreshold: 80,
          criticalThreshold: 100,
        });
      }
    });

    // Add uncategorized expenses
    const uncategorized = categorySpending.get(null);
    if (uncategorized && uncategorized.expense > 0) {
      rows.push({
        categoryName: 'Uncategorized',
        categoryColor: null,
        budget: null,
        spent: uncategorized.expense,
        percentage: 0,
        status: 'on-track',
        warningThreshold: 80,
        criticalThreshold: 100,
      });
    }

    return rows.sort((a, b) => b.spent - a.spent);
  }, [budgets, categorySpending]);

  // Totals
  const totalIncome = incomeRows.reduce((sum, r) => sum + r.actual, 0);
  const totalBudgeted = expenseRows.reduce((sum, r) => sum + (r.budget ?? 0), 0);
  const totalSpent = expenseRows.reduce((sum, r) => sum + r.spent, 0);
  const netExpected = totalIncome - totalBudgeted;
  const netActual = totalIncome - totalSpent;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="container">
          {/* Page header */}
          <div className="mb-6">
            <Link
              to="/app/personal/dashboard"
              className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Personal Dashboard
            </Link>
            <div className="flex items-center justify-between">
              <h1 className="font-display text-2xl font-bold sm:text-3xl">
                Budget Report
              </h1>
            </div>
          </div>

          {/* Period selector */}
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={goToPrevMonth}
              className="flex h-9 w-9 items-center justify-center rounded-btn border bg-card hover:bg-muted/50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[180px] text-center font-display text-lg font-semibold">
              {monthLabel}
            </span>
            <button
              onClick={goToNextMonth}
              className="flex h-9 w-9 items-center justify-center rounded-btn border bg-card hover:bg-muted/50 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : transactions.length === 0 && budgets.length === 0 ? (
            <div className="rounded-card border border-dashed bg-muted/50 p-8 text-center">
              <FileBarChart className="mx-auto h-8 w-8 text-muted-foreground/50" />
              <p className="mt-2 text-sm text-muted-foreground">
                No transactions or budgets found for {monthLabel}.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-card border">
              <table className="w-full text-sm">
                {/* Income Section */}
                <thead>
                  <tr className="bg-sapling-primary-100/10">
                    <th className="px-4 py-3 text-left font-semibold" colSpan={2}>
                      INCOME
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                      Actual
                    </th>
                    <th className="px-4 py-3" colSpan={2} />
                  </tr>
                </thead>
                <tbody>
                  {incomeRows.length === 0 ? (
                    <tr className="border-b">
                      <td className="px-4 py-3 text-muted-foreground italic" colSpan={5}>
                        No income recorded
                      </td>
                    </tr>
                  ) : (
                    incomeRows.map(row => (
                      <tr key={row.categoryName} className="border-b hover:bg-muted/30">
                        <td className="px-4 py-3" colSpan={2}>
                          <span className="flex items-center gap-2">
                            {row.categoryColor && (
                              <span
                                className="inline-block h-3 w-3 rounded-full shrink-0"
                                style={{ backgroundColor: row.categoryColor }}
                              />
                            )}
                            {row.categoryName}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-sapling-primary-100">
                          {formatCurrency(row.actual)}
                        </td>
                        <td className="px-4 py-3" colSpan={2} />
                      </tr>
                    ))
                  )}
                  <tr className="border-b bg-muted/50 font-semibold">
                    <td className="px-4 py-3" colSpan={2}>Total Income</td>
                    <td className="px-4 py-3 text-right text-sapling-primary-100">
                      {formatCurrency(totalIncome)}
                    </td>
                    <td className="px-4 py-3" colSpan={2} />
                  </tr>
                </tbody>

                {/* Expenses Section */}
                <thead>
                  <tr className="bg-sapling-error-100/10">
                    <th className="px-4 py-3 text-left font-semibold">EXPENSES</th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">Budget</th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">Spent</th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">%</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground min-w-[120px]" />
                  </tr>
                </thead>
                <tbody>
                  {expenseRows.length === 0 ? (
                    <tr className="border-b">
                      <td className="px-4 py-3 text-muted-foreground italic" colSpan={5}>
                        No expenses recorded
                      </td>
                    </tr>
                  ) : (
                    expenseRows.map(row => (
                      <tr key={row.categoryName} className="border-b hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-2">
                            {row.categoryColor && (
                              <span
                                className="inline-block h-3 w-3 rounded-full shrink-0"
                                style={{ backgroundColor: row.categoryColor }}
                              />
                            )}
                            {row.categoryName}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.budget !== null ? formatCurrency(row.budget) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatCurrency(row.spent)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.budget !== null ? (
                            <span className={getStatusTextColor(row.status)}>
                              {Math.round(row.percentage)}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.budget !== null && (
                            <div className="h-2 w-full rounded-full bg-muted">
                              <div
                                className={`h-2 rounded-full transition-all ${getStatusColor(row.status)}`}
                                style={{ width: `${Math.min(row.percentage, 100)}%` }}
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                  <tr className="border-b bg-muted/50 font-semibold">
                    <td className="px-4 py-3">Total Expenses</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(totalBudgeted)}</td>
                    <td className="px-4 py-3 text-right text-sapling-error-100">
                      {formatCurrency(totalSpent)}
                    </td>
                    <td className="px-4 py-3" colSpan={2} />
                  </tr>
                </tbody>

                {/* Net Summary */}
                <tfoot>
                  <tr className="bg-card font-bold text-base">
                    <td className="px-4 py-4">Net (Income − Expenses)</td>
                    <td className="px-4 py-4 text-right">{formatCurrency(netExpected)}</td>
                    <td className={`px-4 py-4 text-right ${netActual >= 0 ? 'text-sapling-primary-100' : 'text-sapling-error-100'}`}>
                      {formatCurrency(netActual)}
                    </td>
                    <td className="px-4 py-4" colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Debt Summary — only shown if user has active loans */}
          {!isLoading && activeLoans.length > 0 && (
            <section className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold flex items-center gap-2">
                  <Banknote className="h-5 w-5" />
                  Debt Summary
                </h2>
                <Link
                  to="/app/personal/loans"
                  className="text-sm text-sapling-primary-100 hover:underline"
                >
                  Manage Loans
                </Link>
              </div>
              <div className="mt-4 overflow-x-auto rounded-card border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Loan</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">Balance</th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">Monthly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeLoans.map(loan => (
                      <tr key={loan.id} className="border-b last:border-b-0 hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div>
                            <span className="font-medium">{loan.name}</span>
                            {loan.lender && (
                              <span className="ml-1 text-muted-foreground">({loan.lender})</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground capitalize">
                          {loan.loan_type.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatCurrency(loan.current_balance)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {loan.minimum_payment
                            ? formatCurrency(loan.minimum_payment)
                            : <span className="text-muted-foreground">—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 font-semibold">
                      <td className="px-4 py-3" colSpan={2}>Total Outstanding Debt</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(totalDebt)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(totalMonthlyPayments)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
